import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { adminAPI } from '../config/api';
import swal from 'sweetalert';
import AdminSidebar from '../components/AdminSidebar';
import { showAppToast } from '../utils/notifications';

function AdminMessages() {
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [sortMode, setSortMode] = useState('Newest first');

  useEffect(() => {
    fetchMessages();
  }, []);

  const fetchMessages = async () => {
    setLoading(true);
    try {
      const response = await adminAPI.getMessages();
      setMessages(response.data || []);
    } catch (error) {
      console.error('Failed to fetch contact messages:', error);
      showAppToast({ message: 'Failed to load messages. Please try again.', type: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const filteredMessages = useMemo(() => {
    const term = searchTerm.toLowerCase();
    return messages
      .filter((msg) => (
        msg.name?.toLowerCase().includes(term) ||
        msg.email?.toLowerCase().includes(term) ||
        msg.city?.toLowerCase().includes(term) ||
        msg.country?.toLowerCase().includes(term) ||
        msg.phone?.toLowerCase().includes(term) ||
        msg.message?.toLowerCase().includes(term)
      ))
      .sort((first, second) => {
        if (sortMode === 'Name') return (first.name || '').localeCompare(second.name || '');
        if (sortMode === 'City') return (first.city || '').localeCompare(second.city || '');
        return Number(second.id || 0) - Number(first.id || 0);
      });
  }, [messages, searchTerm, sortMode]);

  const deleteMessage = async (message) => {
    const result = await swal({
      title: 'Delete Message?',
      text: `Delete the message from ${message.name}?`,
      icon: 'warning',
      buttons: ['Cancel', 'Delete'],
      dangerMode: true,
    });

    if (!result) return;

    setSaving(true);
    try {
      await adminAPI.deleteMessage(message.id);
      showAppToast({ message: 'Message deleted successfully', type: 'success', duration: 2400 });
      await fetchMessages();
    } catch (error) {
      showAppToast({ message: error.response?.data?.message || 'Failed to delete message', type: 'error' });
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="admin-layout">
        <AdminSidebar />
        <div className="admin-content px-4 py-16">
          <div className="admin-route-loading-card card-luxury" role="status" aria-live="polite">
            <i className="bi bi-chat-left-text-fill" aria-hidden="true" />
            <strong>Loading messages</strong>
            <p className="text-secondary-theme">Collecting customer enquiry records...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="admin-layout">
      <AdminSidebar />
      <div className="admin-content px-4 py-8">
        <div className="mx-auto max-w-7xl">
          <div className="admin-header">
            <div>
              <p className="text-accent">Admin</p>
              <h1 className="section-title text-4xl text-primary-theme">Contact Messages</h1>
              <p className="text-secondary-theme">Total: {messages.length} messages</p>
            </div>
            <div className="admin-header-actions">
              <Link to="/admin/messages/new" className="btn-luxury admin-add-booking-button">
                <i className="bi bi-plus-lg" />
                Add Message
              </Link>
              <Link to="/admin" className="btn-luxury-outline">
                <i className="bi bi-arrow-left" aria-hidden="true" />
                Back to Dashboard
              </Link>
            </div>
          </div>

          <div className="admin-filter-strip">
            <input
              type="search"
              placeholder="Search by name, email, city, country, phone, or message..."
              value={searchTerm}
              onChange={(event) => setSearchTerm(event.target.value)}
              className="admin-search"
            />
            <select value={sortMode} onChange={(event) => setSortMode(event.target.value)} aria-label="Sort messages">
              <option>Newest first</option>
              <option>Name</option>
              <option>City</option>
            </select>
            <button type="button" onClick={() => setSearchTerm('')} className="btn-luxury-outline">
              Clear
            </button>
          </div>

          {filteredMessages.length ? (
            <div className="admin-messages-grid">
              {filteredMessages.map((msg) => (
                <article key={msg.id} className="admin-message-card card-luxury">
                  <div className="admin-message-header">
                    <div>
                      <h2 className="section-title text-xl text-primary-theme">{msg.name}</h2>
                      <a href={`mailto:${msg.email}`} className="text-secondary-theme no-underline">
                        {msg.email}
                      </a>
                    </div>
                    <span className="admin-message-id">#{msg.id}</span>
                  </div>
                  <div className="admin-message-meta">
                    {msg.phone && (
                      <span>
                        <i className="bi bi-telephone" /> {msg.phone}
                      </span>
                    )}
                    {msg.city && (
                      <span>
                        <i className="bi bi-geo-alt" /> {msg.city}
                      </span>
                    )}
                    {msg.country && (
                      <span>
                        <i className="bi bi-flag" /> {msg.country}
                      </span>
                    )}
                  </div>
                  {msg.message && (
                    <p className="admin-message-body">{msg.message}</p>
                  )}
                  <div className="admin-card-actions" aria-label={`Actions for message ${msg.id}`}>
                    <Link
                      to={`/admin/messages/${msg.id}/edit`}
                      className="admin-icon-action"
                      aria-label={`Edit message from ${msg.name}`}
                      title={`Edit message from ${msg.name}`}
                    >
                      <i className="bi bi-pencil-square" aria-hidden="true" />
                    </Link>
                    <button
                      type="button"
                      className="admin-icon-action admin-icon-action--danger"
                      onClick={() => deleteMessage(msg)}
                      disabled={saving}
                      aria-label={`Delete message from ${msg.name}`}
                      title={`Delete message from ${msg.name}`}
                    >
                      <i className="bi bi-trash3-fill" aria-hidden="true" />
                    </button>
                  </div>
                </article>
              ))}
            </div>
          ) : (
            <div className="card-luxury rounded-lg p-10 text-center text-secondary-theme">
              {searchTerm ? 'No messages found matching your search' : 'No contact messages yet.'}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default AdminMessages;
